import { getSelectedPlayer } from "@Containers/game/builder/functions/player-functions";
import { loadScenario } from "@Containers/game/builder/functions/scenario-functions";
import { getPointAtTime } from "@Containers/game/builder/functions/utilities";
import { Player, ScenarioClip, ScenarioState, StateChangeSource } from "@Containers/game/builder/models/builder-data-types";
import { AppContext } from "@Context/common/general/context";
import { useBuilderContext } from "@Context/game/builder/builder-context";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { clamp, isEmpty, isEqual } from "lodash-es";
import { useContext, useEffect, useRef, useState } from "react";
import { useInterval } from "react-use";

export interface IUseScenarioBuilder {
  clip: ScenarioClip;
  onClipChange?: (clip: ScenarioClip) => void;
}

export interface UseScenarioBuilderReturn {
  // State
  selectedPlayer: Player | undefined;
  isPlaying: boolean;
  scenarioMetadata: ScenarioResponse | undefined;
  viewportDimensions: { width: number; height: number };
  
  // Setters
  setScenarioMetadata: (metadata: ScenarioResponse | undefined) => void;
  
  // Handlers
  handleUndo: () => void;
  handleRedo: () => void;
  handlePlay: () => void;
  pausePlayback: () => void;
  handleCopyClick: () => void;
  handlePasteClick: () => void;
  
  // Context values
  state: any;
  dispatch: any;
  appState: any;
  isVR: boolean;
}

const useScenarioBuilder = ({ 
  clip, 
  onClipChange
}: IUseScenarioBuilder): UseScenarioBuilderReturn => {
  const { state, dispatch } = useBuilderContext();
  const { state: appState } = useContext(AppContext);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | undefined>();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [scenarioMetadata, setScenarioMetadata] = useState<ScenarioResponse | undefined>();
  const isVR = appState.device.isVR;
  
  // Track the last prop value to distinguish internal vs external changes
  const lastPropClipRef = useRef(clip);

  const [viewportDimensions, setViewportDimensions] = useState({
    width: window.innerWidth,
    height: isVR ? window.innerHeight : window.innerHeight - state.navigationHeight,
  });

  // When clip changes internally, propagate it to the parent
  useEffect(() => {
    if (!onClipChange) return;
    
    const isInternalChange = !isEqual(state.clip, lastPropClipRef.current);
    if (isInternalChange) {      
      onClipChange(state.clip);
      lastPropClipRef.current = state.clip; // mark as synchronized
    }
  }, [state.clip, onClipChange]);

  // When clip changes externally, dispatch it to the state
  // NOTE: Order of useEffect is important here. The internal useEffect should run first, otherwise the external useEffect will not see the latest clip.
  useEffect(() => {
    if (!lastPropClipRef.current || (!isEqual(lastPropClipRef.current, clip) && !isEmpty(clip.scenario.players))) {
      loadScenario(clip.scenario, StateChangeSource.Load, state, dispatch, clip.isPreview);
      lastPropClipRef.current = clip;
    }
  }, [clip]);

  // When scenario metadata changes, update the clip metadata
  useEffect(() => {
    if (!scenarioMetadata) return;
    dispatch({
      clip: {
        ...state.clip,
        metadata: { ...scenarioMetadata },
      },
    });
  }, [scenarioMetadata]);

  useEffect(() => {
    const handleResize = () => {
      dispatch({ viewWidth: window.innerWidth });
      setViewportDimensions({
        width: window.innerWidth,
        height: isVR ? window.innerHeight : window.innerHeight - state.navigationHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isVR, state.navigationHeight]);

  useEffect(() => {
    const selectedPlayer = getSelectedPlayer(state);
    setSelectedPlayer(selectedPlayer);
    if (!selectedPlayer) {
      dispatch({
        selectedWaypoint: -1,
        selectedAction: -1,
      });
    }
  }, [state.selectedPlayer, state.clip.scenario.players]);

  useEffect(() => {
    if (state.selectedAction != -1) {
      dispatch({ selectedWaypoint: -1 });
    }
  }, [state.selectedAction]);

  const handleUndo = () => {
    if (state.clip.undoStates.length > 1) {
      const newUndoStates = [...state.clip.undoStates];
      const newRedoStates = [...state.clip.redoStates];
      const currentState = newUndoStates.pop() as ScenarioState;
      newRedoStates.push(currentState);
      const previousState = newUndoStates[newUndoStates.length - 1];
      dispatch({ stateChangeSource: StateChangeSource.Undo, ...previousState, undoStates: newUndoStates, redoStates: newRedoStates });
    }
  };

  const handleRedo = () => {
    if (state.clip.redoStates.length > 0) {
      const newUndoStates = [...state.clip.undoStates];
      const newRedoStates = [...state.clip.redoStates];
      const nextState = newRedoStates.pop() as ScenarioState;
      newUndoStates.push(nextState);
      dispatch({ stateChangeSource: StateChangeSource.Redo, ...nextState, undoStates: newUndoStates, redoStates: newRedoStates });
    }
  };

  useInterval(
    () => {
      if (isPlaying && state.time < state.clip.scenario.endTime) {
        dispatch({ time: clamp(state.time + state.options.playbackResolution * state.options.gameSpeed, 0, state.clip.scenario.endTime) });
      } else {
        setIsPlaying(false);
      }
    },
    isPlaying ? state.options.playbackResolution * 1000 : null
  );

  const handlePlay = () => {
    if (state.time >= state.clip.scenario.endTime) {
      dispatch({ time: 0 });
    }
    setIsPlaying(!isPlaying);
  };

  const pausePlayback = () => {
    if (!isPlaying) return;
    setIsPlaying(false);
  };

  const handleCopyClick = () => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer) return;
    let copiedAction = selectedPlayer.actions[state.selectedAction];
    if (!copiedAction) return;
    copiedAction = { ...copiedAction };
    dispatch({ copiedAction: copiedAction });
  };

  const handlePasteClick = () => {
    const selectedPlayer = getSelectedPlayer(state);
    if (!selectedPlayer) return;
    const copiedAction = state.copiedAction;
    if (!copiedAction) return;
    const newPlayer = { ...selectedPlayer };
    const newActions = [...selectedPlayer.actions];
    newActions.push({ ...copiedAction, startTime: state.time, endTime: state.time + copiedAction.endTime - copiedAction.startTime });
    newPlayer.actions = newActions;
    const newPlayers = new Map<number, Player>();
    state.clip.scenario.players.forEach((value, key) => {
      newPlayers.set(key, key === selectedPlayer.id ? newPlayer : value);
    });
    dispatch({
      clip: {
        ...state.clip,
        scenario: {
          ...state.clip.scenario,
          players: newPlayers,
        },
      },
    });
  };

  useEffect(() => {
    const selectedPlayer = getSelectedPlayer(state);
    if (selectedPlayer) {
      const playerPos = getPointAtTime(selectedPlayer.points, state.time, selectedPlayer.beziers);
      dispatch({ radialAnchor: { x: playerPos.position.x, y: playerPos.position.y } });
    }
  }, [state.time]);

  useEffect(() => {
    if (state.time > state.clip.scenario.endTime) dispatch({ time: state.clip.scenario.endTime });
  }, [state.clip.scenario.endTime]);

  return {
    // State
    selectedPlayer,
    isPlaying,
    scenarioMetadata,
    viewportDimensions,
    
    // Setters
    setScenarioMetadata,
    
    // Handlers
    handleUndo,
    handleRedo,
    handlePlay,
    pausePlayback,
    handleCopyClick,
    handlePasteClick,
    
    // Context values
    state,
    dispatch,
    appState,
    isVR,
  };
}; 

export default useScenarioBuilder;
